import {
  ToggleButton,
  ToggleButtonGroup,
  Tooltip,
  type TooltipProps,
} from '@mui/material';
import type {Theme} from '@mui/material/styles';
import React from 'react';

/** A track segment: named by its visible text, never by an aria-label. */
export type SegmentedOption<T extends string> = {
  value: T;
  label: React.ReactNode;
  'aria-label'?: never;
};

/** A map segment: an icon, named by `aria-label` (also its tooltip). */
export type SegmentedMapOption<T extends string> = {
  value: T;
  label: React.ReactNode;
  'aria-label': string;
};

type AppearanceProps<T extends string> =
  | {
      /** The text segments of the primitive sheet (default). */
      appearance?: 'track';
      options: SegmentedOption<T>[];
    }
  | {
      /**
       * An icon-only control floating on a map (put it in a `MapPanel`):
       * 40px square segments, the selected one an ink fill with its glyph in
       * onInk, each named by its option's `aria-label`, which is also its
       * tooltip.
       */
      appearance: 'map';
      options: SegmentedMapOption<T>[];
    };

export type SegmentedControlProps<T extends string> = AppearanceProps<T> & {
  value: T;
  onChange: (value: T) => void;
  /**
   * Names the group, e.g. the card it switches. Or name it by a visible
   * label with `aria-labelledby` instead.
   */
  'aria-label'?: string;
  'aria-labelledby'?: string;
  /** Stretch across the container, segments sharing the width equally. */
  fullWidth?: boolean;
  /** Ids of text that describes the group (aria-describedby). */
  'aria-describedby'?: string;
  orientation?: 'horizontal' | 'vertical';
  /** Where a `map` segment's tooltip opens (clear of its neighbours). */
  tooltipPlacement?: TooltipProps['placement'];
};

/**
 * One-of-n switch (spec section 3, "Segmented"): a page-coloured track bounded
 * by line.control, the selected segment raised onto the card surface. The
 * selected one is told apart by its raised surface, a hairline ring and its
 * ink (text.primary against text.secondary), and announced by `aria-pressed`,
 * so colour is never the only cue. Every segment keeps the same weight, so
 * selecting one never shifts the others.
 * Exclusive and never empty: clicking the selected segment keeps it.
 */
export const SegmentedControl = <T extends string>({
  value,
  onChange,
  options,
  'aria-label': ariaLabel,
  'aria-labelledby': ariaLabelledBy,
  'aria-describedby': ariaDescribedBy,
  fullWidth = false,
  appearance = 'track',
  orientation = 'horizontal',
  tooltipPlacement = 'top',
}: SegmentedControlProps<T>) => {
  const map = appearance === 'map';
  // The type says so; this catches a cast. An unnamed icon button is silent.
  if (map && options.some((option) => !option['aria-label'])) {
    throw new Error('ui/SegmentedControl: every map segment needs aria-label');
  }
  return (
    <ToggleButtonGroup
      exclusive
      size="small"
      value={value}
      fullWidth={fullWidth}
      orientation={orientation}
      aria-label={ariaLabel}
      aria-labelledby={ariaLabelledBy}
      aria-describedby={ariaDescribedBy}
      onChange={(_, next: T | null) => {
        if (next !== null) {
          onChange(next);
        }
      }}
      sx={map ? MAP_GROUP_SX : trackGroupSx(fullWidth)}>
      {options.map((option) => {
        const segment = (
          <ToggleButton
            key={option.value}
            value={option.value}
            // Track segments are named by their visible text (label in name).
            aria-label={map ? option['aria-label'] : undefined}
            sx={map ? MAP_SEGMENT_SX : TRACK_SEGMENT_SX}>
            {option.label}
          </ToggleButton>
        );
        return map && option['aria-label'] ? (
          <Tooltip
            key={option.value}
            title={option['aria-label']}
            placement={tooltipPlacement}>
            {segment}
          </Tooltip>
        ) : (
          segment
        );
      })}
    </ToggleButtonGroup>
  );
};

const trackGroupSx = (fullWidth: boolean) => ({
  p: '2px',
  gap: '2px',
  borderRadius: '6px',
  border: 1,
  borderColor: 'border.control',
  bgcolor: 'background.default',
  // Full width: one line, the segments sharing it equally (MUI's own
  // fullWidth gives each button width 100%, which wraps them into a
  // column here).
  flexWrap: fullWidth ? 'nowrap' : 'wrap',
  '& .MuiToggleButtonGroup-grouped': {
    // MUI joins grouped buttons edge to edge; the segments float apart.
    border: 0,
    borderRadius: '4px !important',
    m: 0,
    ...(fullWidth && {
      flexGrow: 1,
      flexShrink: 1,
      flexBasis: 0,
      minWidth: 0,
    }),
  },
});

const TRACK_SEGMENT_SX = {
  px: 1,
  py: 0.25,
  minHeight: 26,
  typography: 'caption',
  lineHeight: 1.5,
  textTransform: 'none',
  whiteSpace: 'nowrap',
  fontWeight: 'fontWeightMedium',
  color: 'text.secondary',
  '&.Mui-selected, &.Mui-selected:hover': {
    bgcolor: 'background.paper',
    color: 'text.primary',
    boxShadow: (theme: Theme) => `0 0 0 1px ${theme.palette.divider}`,
  },
  // Tight to the segment so the ring does not run into its
  // neighbours; `&&` outranks the theme's global ring rule.
  '&&:focus-visible': {outlineOffset: 0},
} as const;

// The map panel draws the edge and clips the corners; the segments sit flush.
const MAP_GROUP_SX = {
  '& .MuiToggleButtonGroup-grouped': {border: 0, borderRadius: 0, m: 0},
} as const;

const MAP_SEGMENT_SX = {
  width: 40,
  height: 40,
  p: 0,
  color: 'text.secondary',
  // The glyph at the map controls' size (MapControlButton's icons).
  '& svg': {width: 20, height: 20},
  // Inset ring: the panel clips anything outside its edge.
  '&&:focus-visible': {outlineOffset: -2},
  // The selection is a state, so it is ink (the brand fill is never a
  // state); aria-pressed says it to assistive tech.
  '&.Mui-selected, &.Mui-selected:hover': {
    bgcolor: 'ink.main',
    color: 'ink.onInk',
  },
} as const;
